import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from 'tss-react/mui';
import { green } from "@mui/material/colors";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import { i18n } from "../../translate/i18n";
import ConfirmationMultiCompaniesModal from "../ConfirmationMultiCompaniesModal";
import CreateOrEditCompanyUsersModal from "../CreateOrEditCompanyUsersModal"
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ToastError from "../../toast/error/toastError";


const useStyles = makeStyles()((theme) => {
	return {
		root: {
			display: "flex",
			flexWrap: "wrap",
		},
		multFieldLine: {
			display: "flex",
			"& > *:not(:last-child)": {
				marginRight: theme.spacing(1),
			},
		},
		btnWrapper: {
			position: "relative",
		},
		buttonProgress: {
			position: "absolute",
			top: "50%",
			left: "50%",
			marginTop: -12,
			marginLeft: -12,
		},
		formControl: {
			margin: theme.spacing(1),
			minWidth: 120,
		}
	}
});


const CreateOrEditCompanyModal = ({ open, onClose, companyId }) => {

	const { classes } = useStyles();

	const [users, setUsers] = useState([]);

	const [openEditUserModal, setOpenEditUserModal] = useState(false);
	const [userIdToEdit, setUserIdToEdit] = useState(null);

	// Função para formatar o último acesso
	const formatLastAccess = (updatedAt) => {
		if (!updatedAt) return null;

		const lastAccessDate = new Date(updatedAt);
		const now = new Date();
		const diffInMs = now - lastAccessDate;
		const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
		const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
		const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

		// Data formatada
		const dateFormatted = lastAccessDate.toLocaleDateString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});

		const timeFormatted = lastAccessDate.toLocaleTimeString('pt-BR', {
			hour: '2-digit',
			minute: '2-digit'
		});

		// Tempo relativo
		let timeRelative = '';
		if (diffInMinutes < 1) {
			timeRelative = 'agora mesmo';
		} else if (diffInMinutes < 60) {
			timeRelative = `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
		} else if (diffInHours < 24) {
			timeRelative = `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
		} else if (diffInDays === 1) {
			timeRelative = 'há 1 dia';
		} else if (diffInDays < 30) {
			timeRelative = `há ${diffInDays} dias`;
		} else if (diffInDays < 365) {
			const months = Math.floor(diffInDays / 30);
			timeRelative = `há ${months} ${months === 1 ? 'mês' : 'meses'}`;
		} else {
			const years = Math.floor(diffInDays / 365);
			timeRelative = `há ${years} ${years === 1 ? 'ano' : 'anos'}`;
		}

		// Se for mais de 1 dia, mostrar data completa
		if (diffInDays > 1) {
			return `${timeRelative} (${dateFormatted} às ${timeFormatted})`;
		}

		return timeRelative;
	};

	useEffect(() => {
		const fetchUser = async () => {
			if (!companyId) return;
			try {
				const { data } = await api.get(`/company-users/${companyId}`);
				setUsers(data)
			} catch (err) {
				ToastError(err);
			}
		};

		fetchUser();
	}, [companyId, open]);

	const handleClose = () => {
		onClose();
	};

	const handleEditCompanyUsers = (id) => {
		setUserIdToEdit(id);
		setOpenEditUserModal(true);
	};

	const handleCloseCompanyUsersModal = () => {
		setUserIdToEdit(null);
		setOpenEditUserModal(false);
	};

	return (
		<div className={classes.root}>
			<CreateOrEditCompanyUsersModal
				open={openEditUserModal}
				onClose={handleCloseCompanyUsersModal}
				aria-labelledby="form-dialog-title"
				userId={userIdToEdit}
			/>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="body"
				BackdropProps={{
					style: {
						backdropFilter: 'blur(3px)',
						backgroundColor: 'rgba(0, 0, 30, 0.4)',
					},
				}}
			>
				<DialogTitle id="form-dialog-title">{'Editar usuário da empresa'}</DialogTitle>
				<Box sx={{ flexGrow: 1, maxWidth: '100%' }}>
					<Grid item xs={12} md={12}>
						<List sx={{ maxHeight: '500px', overflow: 'auto', padding: '20px' }}>
							{users.map((user) => (
								<ListItem
									key={user.id}
									secondaryAction={
										<IconButton edge="end" onClick={e => handleEditCompanyUsers(user.id)} aria-label="edit">
											<EditIcon />
										</IconButton>
									}
									sx={{
										borderRadius: 2,
										mb: 1,
										'&:hover': {
											backgroundColor: 'action.hover',
										}
									}}
								>
									<ListItemAvatar>
										<Avatar src={user?.media} alt={user?.name} />
									</ListItemAvatar>
									<ListItemText
										primary={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
												<Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
													{user?.name}
												</Typography>
												<Chip
													label={user?.profile}
													size="small"
													sx={{
														height: 20,
														fontSize: '0.688rem',
														fontWeight: 600,
													}}
													color={user?.profile === 'admin' ? 'error' : 'default'}
												/>
											</Box>
										}
										secondary={
											<Box sx={{ mt: 0.5 }}>
												<Typography variant="caption" component="div" color="text.secondary">
													{user?.email}
												</Typography>
												{user?.updatedAt && (
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
														<AccessTimeIcon sx={{ fontSize: 14 }} color="action" />
														<Typography variant="caption" color="text.secondary">
															Último acesso: {formatLastAccess(user.updatedAt)}
														</Typography>
													</Box>
												)}
											</Box>
										}
									/>
								</ListItem>
							))}
						</List>
					</Grid>
				</Box>
			</Dialog>
		</div>
	);
};

export default CreateOrEditCompanyModal;
